/**
 * Minimal, dependency-free stand-in for zustand's `create()`.
 *
 * The Efforts / Field Sales filter stores (and the Efforts upload-progress store)
 * were written against zustand. Only `@tanstack/react-query` was approved as a
 * new dependency, so this module provides the small subset of the zustand API
 * those stores use, built on React 18's `useSyncExternalStore`:
 *
 *   const useStore = create((set, get) => ({ count: 0, inc: () => set((s) => ({ count: s.count + 1 })) }));
 *   const count = useStore((s) => s.count);   // selector subscription
 *   const all = useStore();                   // whole state
 *   useStore.getState(); useStore.setState(partial); useStore.subscribe(listener);
 *
 * `set` shallow-merges a partial object (or the result of an updater function)
 * into the current state, exactly like zustand. Components re-render only when
 * the selected slice changes by `Object.is`.
 */
import { useSyncExternalStore } from "react";

const identity = (state) => state;

export function create(initializer) {
  let state;
  const listeners = new Set();

  const getState = () => state;

  const setState = (partial, replace = false) => {
    const next = typeof partial === "function" ? partial(state) : partial;
    if (Object.is(next, state)) return;
    const previous = state;
    state = replace || typeof next !== "object" || next === null ? next : { ...state, ...next };
    listeners.forEach((listener) => listener(state, previous));
  };

  const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  state = initializer(setState, getState, { getState, setState, subscribe });

  function useStore(selector = identity) {
    // The selector runs against the live snapshot on every render, so an inline
    // selector (a new function each render) is fine: the returned slice is what
    // React compares, not the selector itself.
    return useSyncExternalStore(
      subscribe,
      () => selector(state),
      () => selector(state),
    );
  }

  useStore.getState = getState;
  useStore.setState = setState;
  useStore.subscribe = subscribe;

  return useStore;
}

export default create;
